import React from "react";
import { motion } from "motion/react";
import { getProcessCapiData } from "../../services/processService";
import { CAPI_DIMENSIONS } from "./dimensionAllocations";

const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

// The endpoint returns one row per job-per-path, so the same job can repeat
// (with or without CAPI data) across rows. Collapse to one entry per job
// name, preferring whichever repeat actually carries CAPI values.
function normalizeRows(raw) {
  const byName = new Map();
  (Array.isArray(raw) ? raw : []).forEach((entry) => {
    const job = entry?.job;
    const name = job?.name;
    if (!name) return;
    const capi = job.capi
      ? {
          authority: clamp(Number(job.capi.authority)),
          power: clamp(Number(job.capi.power)),
          influence: clamp(Number(job.capi.influence)),
        }
      : null;
    const existing = byName.get(name);
    if (!existing) byName.set(name, { name, capi });
    else if (!existing.capi && capi) existing.capi = capi;
  });
  return Array.from(byName.values());
}

const cardStyle = {
  border: "1px solid var(--gray-200)",
  borderRadius: 10,
  padding: "14px 16px",
  background: "var(--color-surface)",
};

/**
 * Job × CAPI (Authority / Power / Influence) overview for a process — which
 * jobs in this process carry the most decision-making weight.
 * - readOnly=true → View Process: display-only, no editing.
 */
export default function JobCapiPanel({ processId, isActive, readOnly = true }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getProcessCapiData(processId);
      setRows(normalizeRows(res));
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load job CAPI data");
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    const shouldLoad = isActive === undefined ? true : isActive;
    if (shouldLoad && processId && !loaded && !loading) load();
  }, [isActive, processId, loaded, loading, load]);

  const scored = React.useMemo(() => rows.filter((r) => r.capi), [rows]);
  const unscored = React.useMemo(() => rows.filter((r) => !r.capi), [rows]);

  const ranked = React.useMemo(
    () =>
      scored
        .slice()
        .sort(
          (a, b) =>
            b.capi.authority + b.capi.power + b.capi.influence -
            (a.capi.authority + a.capi.power + a.capi.influence),
        ),
    [scored],
  );

  const topByDimension = React.useMemo(() => {
    const out = {};
    CAPI_DIMENSIONS.forEach((d) => {
      let bestValue = null;
      scored.forEach((r) => {
        if (bestValue === null || r.capi[d.key] > bestValue) bestValue = r.capi[d.key];
      });
      out[d.key] =
        bestValue === null
          ? []
          : scored.filter((r) => r.capi[d.key] === bestValue);
    });
    return out;
  }, [scored]);

  return (
    <div style={{ padding: "2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
          Job CAPI — Authority, Power &amp; Influence
        </h3>
        <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
          {readOnly ? "Which jobs in this process carry the most decision-making weight" : ""}
        </span>
      </div>

      {loading && <p style={{ color: "var(--gray-500)" }}>Loading job CAPI data…</p>}
      {error && (
        <div
          style={{
            margin: "10px 0",
            padding: "8px 12px",
            background: "var(--red-tint-50)",
            border: "1px solid var(--red-tint-200)",
            borderRadius: 8,
            color: "var(--red-ink-700)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && loaded && rows.length === 0 && (
        <p style={{ color: "var(--gray-400)", fontStyle: "italic" }}>No jobs found for this process.</p>
      )}

      {!loading && scored.length > 0 && (
        <>
          {/* Top job per dimension */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
              gap: 12,
              marginBottom: 18,
            }}
          >
            {CAPI_DIMENSIONS.map((d) => {
              const top = topByDimension[d.key] || [];
              return (
                <div key={d.key} style={{ ...cardStyle, borderTop: `3px solid ${d.accent}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gray-400)" }}>
                    Highest {d.label}
                  </div>
                  {top.length > 0 ? (
                    <>
                      <ol style={{ fontSize: 15, fontWeight: 700, margin: "4px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 2 }}>
                        {top.map((r) => (
                          <li key={r.name}>{r.name}</li>
                        ))}
                      </ol>
                      <div style={{ fontSize: 22, fontWeight: 700, color: d.accent, marginTop: 2 }}>
                        {top[0].capi[d.key]}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: "var(--gray-400)", fontStyle: "italic", marginTop: 6 }}>—</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Ranked bar chart */}
          <div style={{ ...cardStyle, marginBottom: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Jobs ranked by CAPI</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
              {CAPI_DIMENSIONS.map((d) => (
                <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--gray-500)" }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: d.accent, display: "inline-block" }} />
                  {d.label}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {ranked.map((r) => (
                <div key={r.name}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{r.name}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {CAPI_DIMENSIONS.map((d) => {
                      const value = r.capi[d.key];
                      return (
                        <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 62, fontSize: 11, color: "var(--gray-500)", flexShrink: 0 }}>{d.label}</span>
                          <div style={{ flex: 1, background: "var(--gray-100)", borderRadius: 5, overflow: "hidden", height: 14 }}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${value}%` }}
                              transition={{ type: "spring", stiffness: 260, damping: 28 }}
                              style={{ height: "100%", background: d.accent, borderRadius: 5 }}
                            />
                          </div>
                          <span style={{ width: 26, fontSize: 12, fontWeight: 600, textAlign: "right", flexShrink: 0 }}>
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Full table */}
      {!loading && rows.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>All jobs</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--gray-500)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  <th style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-200)" }}>Job</th>
                  {CAPI_DIMENSIONS.map((d) => (
                    <th key={d.key} style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-200)" }}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ranked.map((r) => (
                  <tr key={r.name}>
                    <td style={{ padding: "8px", borderBottom: "1px solid var(--gray-100)" }}>{r.name}</td>
                    {CAPI_DIMENSIONS.map((d) => (
                      <td key={d.key} style={{ padding: "8px", borderBottom: "1px solid var(--gray-100)", fontWeight: 600 }}>
                        {r.capi[d.key]}
                      </td>
                    ))}
                  </tr>
                ))}
                {unscored.map((r) => (
                  <tr key={r.name}>
                    <td style={{ padding: "8px", borderBottom: "1px solid var(--gray-100)", color: "var(--gray-500)" }}>{r.name}</td>
                    <td colSpan={CAPI_DIMENSIONS.length} style={{ padding: "8px", borderBottom: "1px solid var(--gray-100)", color: "var(--gray-400)", fontStyle: "italic" }}>
                      Not scored
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
